import { isEmpty } from 'lodash';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { SanitizedHtml, StrapiImg, ZendeskLink } from 'components';
import { useUser } from 'hooks';
import { routes } from 'router';
import { appService } from 'services';

const FooterBanner = () => {
  const bannerData = useSelector((state) => state.common.footerBanner);

  return (
    <div className="relative footer-banner-container main-container">
      <div className="footer-banner">
        <div className="w-full lg:w-1/2 2xl:w-1/3 z-1">
          <h2 className="h2 underlined-yellow mb-8">{bannerData.title}</h2>

          <SanitizedHtml content={bannerData.content} className="html-content mb-8" />

          {bannerData?.link?.link && (
            <Link
              to={bannerData.link.link}
              className="btn secondary w-fit mx-auto uppercase text-sm lg:text-base lg:mx-initial"
            >
              {bannerData.link.buttonText}
            </Link>
          )}
        </div>

        {bannerData?.image?.url && (
          <div className="hidden lg:block image-container">
            <StrapiImg image={bannerData.image} />
          </div>
        )}
      </div>
    </div>
  );
};

export const Footer = () => {
  const { t } = useTranslation();
  const { isLoggedIn } = useUser();
  const bannerData = useSelector((state) => state.common.footerBanner);
  const currentYear = new Date().getFullYear();

  return (
    <>
      {!isEmpty(bannerData) && <FooterBanner />}
      <footer className="footer bg-primary-500 print:hidden">
        <div className="footer-main-container main-container">
          <Link to={routes.home.path} className="flex-shrink-0">
            <img
              src={appService.loadBrandImage('logoDark')}
              alt={t(`images.${appService.brand()}.logoAlt`)}
              width="320"
              height="215"
              className="w-48 2xl:w-60"
            />
          </Link>

          <div className="flex justify-between flex-wrap grow gap-8 lg:gap-y-8 2xl:w-3/5 2xl:gap-x-16 2xl:grow-0">
            <div className="footer-col">
              <h2 className="footer-col-title">{appService.brand()}</h2>
              <Link to={routes.about.path} className="hover:underline focus:underline">
                {t('footer.categories.company.links.about')}
              </Link>
              <Link to={routes.trustUs.path} className="hover:underline focus:underline">
                {t('footer.categories.company.links.trustUs')}
              </Link>
            </div>

            <div className="footer-col">
              <h2 className="footer-col-title">{t('footer.categories.info.name')}</h2>
              {isLoggedIn && (
                <ZendeskLink className="hover:underline focus:underline">
                  {t('footer.categories.info.links.help')}
                </ZendeskLink>
              )}
              <Link to={routes.insurance.path} className="hover:underline focus:underline">
                {t('footer.categories.info.links.insurance')}
              </Link>
              <Link to={routes.termsOfSales.path} className="hover:underline focus:underline">
                {t('footer.categories.info.links.termsOfSales')}
              </Link>
            </div>

            {isLoggedIn && (
              <div className="footer-col">
                <h2 className="footer-col-title">{t('footer.categories.convenient.name')}</h2>
                <Link to={routes.dashboard.children.orders.path} className="hover:underline focus:underline">
                  {t('footer.categories.convenient.links.orders')}
                </Link>
                <Link to={routes.dashboard.children.benefits.path} className="hover:underline focus:underline">
                  {t('footer.categories.convenient.links.benefits')}
                </Link>
                <ZendeskLink className="hover:underline focus:underline">
                  {t('footer.categories.convenient.links.contact')}
                </ZendeskLink>
              </div>
            )}

            <div className="footer-col">
              <h2 className="footer-col-title">{t('footer.categories.contactDetails.name')}</h2>
              <address className="not-italic">
                <Trans
                  i18nKey="footer.categories.contactDetails.address"
                  values={{ brand: appService.brand() }}
                  components={{ 1: <br /> }}
                />
              </address>
            </div>
          </div>
        </div>

        <div className="w-full text-sm text-center text-white border-t border-white pt-4 px-12 md:px:8 pb-8">
          {appService.brand()} {currentYear} -{' '}
          <Link to={routes.terms.path} className="hover:underline focus:underline">
            {t('footer.legal.terms')}
          </Link>{' '}
          -{' '}
          <Link to={routes.terms.path} className="hover:underline focus:underline">
            {t('footer.legal.confidentiality')}
          </Link>
        </div>
      </footer>
    </>
  );
};

export default Footer;
